import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Package, Download, Sparkles, ShoppingBag, ArrowRight, FileDown, Check, Settings, CheckSquare, Square } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, downloadsRes] = await Promise.all([
          axios.get(`${API}/orders`),
          axios.get(`${API}/downloads`)
        ]);
        setOrders(ordersRes.data);
        setDownloads(downloadsRes.data);
      } catch (e) {
        console.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDownload = async (design) => {
    try {
      const response = await axios.get(`${API}/downloads/${design.id}`);
      window.open(response.data.download_url, '_blank');
      toast.success(`Downloading ${design.title}`);
    } catch (e) {
      toast.error("Download failed. Please try again.");
    }
  };

  const toggleDesignSelection = (designId) => {
    setSelectedDesigns((prev) =>
      prev.includes(designId)
        ? prev.filter((id) => id !== designId)
        : [...prev, designId]
    );
  };

  const selectAllDesigns = () => {
    if (selectedDesigns.length === downloads.length) {
      setSelectedDesigns([]);
    } else {
      setSelectedDesigns(downloads.map((d) => d.id));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedDesigns.length === 0) {
      toast.error("Please select designs to download");
      return;
    }

    setBulkDownloading(true);
    try {
      const response = await axios.post(`${API}/downloads/bulk`, {
        design_ids: selectedDesigns,
      });

      // Download each file
      response.data.designs.forEach((design, index) => {
        setTimeout(() => {
          window.open(design.download_url, '_blank');
        }, index * 500); // Stagger downloads to prevent browser blocking
      });

      toast.success(`Downloading ${response.data.total} design${response.data.total !== 1 ? 's' : ''}`);
      setSelectedDesigns([]);
    } catch (e) {
      toast.error("Bulk download failed. Please try again.");
    } finally {
      setBulkDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="dashboard-page">
      <Navbar />

      <main className="pt-32 pb-16 container-padding">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-12">
            <div>
              <p className="overline mb-2">DASHBOARD</p>
              <h1 
                className="text-3xl sm:text-4xl font-semibold tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Welcome, {user?.name || "Creator"}
              </h1>
              <p className="mt-2 text-zinc-600">
                Manage your purchases and downloads.
              </p>
            </div>
            <Link
              to="/profile"
              className="btn-secondary text-sm py-2 px-4 flex items-center gap-2"
              data-testid="profile-settings-link"
            >
              <Settings className="w-4 h-4" />
              Settings
            </Link>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
            <Link
              to="/generator"
              className="p-6 bg-white border border-zinc-200 rounded-xl hover:border-indigo-500 transition-colors group"
              data-testid="quick-action-generate"
            >
              <Sparkles className="w-8 h-8 text-indigo-500 mb-4" />
              <h3 className="font-medium mb-1 group-hover:text-indigo-600 transition-colors">
                Generate Design
              </h3>
              <p className="text-sm text-zinc-500">
                Create with AI
              </p>
            </Link>

            <Link
              to="/marketplace"
              className="p-6 bg-white border border-zinc-200 rounded-xl hover:border-zinc-900 transition-colors group"
              data-testid="quick-action-browse"
            >
              <ShoppingBag className="w-8 h-8 text-zinc-700 mb-4" />
              <h3 className="font-medium mb-1">
                Browse Marketplace
              </h3>
              <p className="text-sm text-zinc-500">
                Find designs
              </p>
            </Link>

            <Link
              to="/cart"
              className="p-6 bg-white border border-zinc-200 rounded-xl hover:border-zinc-900 transition-colors group"
              data-testid="quick-action-cart"
            >
              <Package className="w-8 h-8 text-zinc-700 mb-4" />
              <h3 className="font-medium mb-1">
                View Cart
              </h3>
              <p className="text-sm text-zinc-500">
                Continue shopping
              </p>
            </Link>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="downloads" className="w-full">
            <TabsList className="w-full justify-start border-b border-zinc-200 bg-transparent h-auto p-0 mb-8">
              <TabsTrigger 
                value="downloads" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 rounded-none bg-transparent px-4 py-3"
              >
                <Download className="w-4 h-4 mr-2" />
                My Downloads
              </TabsTrigger>
              <TabsTrigger 
                value="orders" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 rounded-none bg-transparent px-4 py-3"
              >
                <Package className="w-4 h-4 mr-2" />
                Order History
              </TabsTrigger>
            </TabsList>

            {/* Downloads Tab */}
            <TabsContent value="downloads">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton-pulse h-64 rounded-xl"></div>
                  ))}
                </div>
              ) : downloads.length === 0 ? (
                <div className="empty-state bg-white rounded-2xl border border-zinc-200 py-16">
                  <Download className="w-16 h-16 text-zinc-300 mb-4" />
                  <h3 className="text-xl font-medium mb-2">No downloads yet</h3>
                  <p className="text-zinc-500 mb-6">
                    Purchase designs to download them here.
                  </p>
                  <Link to="/marketplace" className="btn-primary inline-flex items-center gap-2">
                    Browse Designs
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <>
                  {/* Bulk Actions Bar */}
                  <div className="bg-white p-4 rounded-xl border border-zinc-200 mb-6 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={selectAllDesigns}
                        className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
                        data-testid="select-all-btn"
                      >
                        {selectedDesigns.length === downloads.length ? (
                          <CheckSquare className="w-5 h-5 text-indigo-500" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                        {selectedDesigns.length === downloads.length ? "Deselect All" : "Select All"}
                      </button>
                      {selectedDesigns.length > 0 && (
                        <span className="text-sm text-zinc-500">
                          {selectedDesigns.length} selected
                        </span>
                      )}
                    </div>
                    <button
                      onClick={handleBulkDownload}
                      disabled={selectedDesigns.length === 0 || bulkDownloading}
                      className="btn-primary text-sm py-2 px-4 flex items-center gap-2 disabled:opacity-50"
                      data-testid="bulk-download-btn"
                    >
                      {bulkDownloading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"></div>
                      ) : (
                        <FileDown className="w-4 h-4" />
                      )}
                      Download Selected ({selectedDesigns.length})
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {downloads.map((design) => (
                      <div
                        key={design.id}
                        className={`bg-white border rounded-xl overflow-hidden transition-all ${
                          selectedDesigns.includes(design.id)
                            ? "border-indigo-500 ring-2 ring-indigo-500/20"
                            : "border-zinc-200"
                        }`}
                        data-testid={`download-item-${design.id}`}
                      >
                        <div 
                          className="aspect-square bg-zinc-100 relative cursor-pointer"
                          onClick={() => toggleDesignSelection(design.id)}
                        >
                          <img
                            src={design.image_url}
                            alt={design.title}
                            className="w-full h-full object-cover"
                          />
                          {/* Selection Checkbox */}
                          <div className="absolute top-3 left-3">
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
                              selectedDesigns.includes(design.id)
                                ? "bg-indigo-500"
                                : "bg-white/80 border border-zinc-300"
                            }`}>
                              {selectedDesigns.includes(design.id) && (
                                <Check className="w-4 h-4 text-white" />
                              )}
                            </div>
                          </div>
                          <div className="absolute top-3 right-3">
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Purchased
                            </span>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="font-medium mb-1">{design.title}</h3>
                          <p className="text-sm text-zinc-500 mb-4">{design.category}</p>
                          <button
                            onClick={() => handleDownload(design)}
                            className="w-full btn-secondary text-sm py-2 flex items-center justify-center gap-2"
                            data-testid={`download-btn-${design.id}`}
                          >
                            <FileDown className="w-4 h-4" />
                            Download PNG
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton-pulse h-32 rounded-xl"></div>
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <div className="empty-state bg-white rounded-2xl border border-zinc-200 py-16">
                  <Package className="w-16 h-16 text-zinc-300 mb-4" />
                  <h3 className="text-xl font-medium mb-2">No orders yet</h3>
                  <p className="text-zinc-500 mb-6">
                    Start browsing the marketplace to find amazing designs.
                  </p>
                  <Link to="/marketplace" className="btn-primary inline-flex items-center gap-2">
                    Browse Designs
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <OrderCard key={order.id} order={order} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function OrderCard({ order }) {
  const [expanded, setExpanded] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOrderDetails = async () => {
    if (designs.length > 0) {
      setExpanded(!expanded);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${order.id}`);
      setDesigns(response.data.designs || []);
      setExpanded(true);
    } catch (e) {
      console.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div 
      className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
      data-testid={`order-${order.id}`}
    >
      {/* Header */}
      <div
        className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer hover:bg-zinc-50 transition-colors"
        onClick={fetchOrderDetails}
      >
        <div>
          <p className="text-sm text-zinc-500 mb-1">
            Order #{order.id.slice(0, 8).toUpperCase()}
          </p>
          <p className="font-medium">
            {order.items?.length || 0} design{order.items?.length !== 1 ? "s" : ""}
          </p>
          <p className="text-sm text-zinc-500 mt-1">
            {formatDate(order.created_at)}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="price-tag text-lg font-semibold">${order.total?.toFixed(2)}</p>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Completed
            </span>
          </div>
          <ArrowRight className={`w-5 h-5 text-zinc-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-zinc-200 p-6 bg-zinc-50 animate-fade-in">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-6 h-6 border-2 border-zinc-200 border-t-indigo-500 rounded-full spinner"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {designs.map((design) => (
                <div
                  key={design.id}
                  className="flex items-center gap-4 bg-white p-4 rounded-lg"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-zinc-100 shrink-0">
                    <img
                      src={design.image_url}
                      alt={design.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{design.title}</p>
                    <p className="text-sm text-zinc-500">{design.category}</p>
                  </div>
                  <a
                    href={design.image_url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 btn-secondary text-sm py-2 px-4 flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
